import { ProductGroupAttribute } from './ProductGroupAttribute';
import { ProductGroupMember } from './ProductGroupMember';

/**
 * ProductGroup DTO - the admin view of a product variation group.
 *
 * `priceMin`/`priceMax`/`totalStock` are computed server-side from the active members, so the
 * list view can show a range without loading every member's full product.
 */
export class ProductGroup {
  constructor(data = {}) {
    this.id = data.id || null;
    this.name = data.name || null;
    this.description = data.description || null;
    this.slug = data.slug || null;
    this.isActive = data.isActive !== undefined ? Boolean(data.isActive) : true;
    this.defaultProductId = data.defaultProductId || null;
    this.attributes = (data.attributes || []).map(ProductGroupAttribute.fromJSON).filter(Boolean);
    this.members = (data.members || []).map(ProductGroupMember.fromJSON).filter(Boolean);
    this.priceMin = data.priceMin !== undefined && data.priceMin !== null ? Number(data.priceMin) : null;
    this.priceMax = data.priceMax !== undefined && data.priceMax !== null ? Number(data.priceMax) : null;
    this.totalStock = data.totalStock !== undefined && data.totalStock !== null ? Number(data.totalStock) : 0;
    this.createdAt = data.createdAt || null;
    this.updatedAt = data.updatedAt || null;
  }

  static fromJSON(json) {
    if (!json) {
      return null;
    }

    return new ProductGroup({
      id: json.id,
      name: json.name,
      description: json.description,
      slug: json.slug,
      isActive: json.isActive,
      defaultProductId: json.defaultProductId,
      attributes: json.attributes,
      members: json.members,
      priceMin: json.priceMin,
      priceMax: json.priceMax,
      totalStock: json.totalStock,
      createdAt: json.createdAt,
      updatedAt: json.updatedAt,
    });
  }

  /**
   * True when the group spans more than one price.
   * @returns {boolean}
   */
  hasPriceRange() {
    return this.priceMin !== null && this.priceMax !== null && this.priceMin !== this.priceMax;
  }

  /**
   * The member fronting the group card, falling back to the first member.
   * @returns {ProductGroupMember|null}
   */
  getDefaultMember() {
    if (!this.members.length) {
      return null;
    }

    return this.members.find((member) => member.productId === this.defaultProductId)
      || this.members[0];
  }

  toJSON() {
    return {
      id: this.id,
      name: this.name,
      description: this.description,
      isActive: this.isActive,
      defaultProductId: this.defaultProductId,
      attributes: this.attributes.map((attribute) => attribute.toJSON()),
      members: this.members.map((member) => member.toJSON()),
    };
  }
}
